import { getAnalyticsRoiHero } from 'store/analytics/actions'
import type { GetAnalyticsRoiDataResponse, GetAnalyticsRoiPayload } from 'store/analytics/types'
import { useSelector } from 'store/hooks'

import type { UseQuerySelectOptions, UseQuerySelectResult } from 'hooks'

import { useQuerySelect } from 'hooks'

import { useAnalyticsAreaParam, withAnalyticsArea } from './useAnalyticsAreaParam'

type UseAnalyticsParams = GetAnalyticsRoiPayload

type UseAnalyticsResult = {
  data: GetAnalyticsRoiDataResponse | null
} & UseQuerySelectResult<UseAnalyticsParams>

/**
 * The ROI hero's own ROI request.
 *
 * Identical in shape to `useAnalyticsRoi`, pointed at a SECOND redux slot. The hero owns
 * its own period (default: the current billing period, where the spend denominator is
 * exact) while the ROI metric cards follow the page's date range, so the two consumers
 * fetch different windows from the same endpoint. Sharing one slot between them is a
 * last-writer-wins race, not a saved request — see `Actions.GET_ANALYTICS_ROI_HERO`.
 *
 * ⚠️ `enabled: false` sets `isLoading` to FALSE (see `useQuerySelect`), so a caller
 * gating the request on "the billing period boundaries have arrived" must not derive its
 * skeleton from `isLoading` alone — `!data` is what distinguishes "nothing yet" from
 * "nothing coming". `RoiHero`'s call site does exactly that.
 */
export const useAnalyticsRoiHero = (
  params: UseAnalyticsParams,
  options?: UseQuerySelectOptions,
): UseAnalyticsResult => {
  /*
    The page-global drawn area (VRG-480), merged in HERE rather than at the call site.
    See `useAnalyticsAreaParam` — the merge is inside the hook so a new analytics card
    cannot forget it, and so `useQuerySelect`'s params comparison sees the ring and
    refetches when it changes.
  */
  const areaParam = useAnalyticsAreaParam()
  const request = withAnalyticsArea(params, areaParam)

  const result = useQuerySelect(getAnalyticsRoiHero, request, options)
  const data = useSelector(state => state.analytics.roiHero)

  return {
    data,
    ...result,
  }
}
